import React, {useContext, useEffect, useMemo} from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  IconButton,
  InputAdornment,
  TextField,
  Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import {FloatButtonContext} from '@assets/contexts/floatButtonContext';
import {useWidgetLabelChanged} from '@assets/pages/Widget/WidgetLabelChangedProvider';
import IconSendOutline from '@assets/components/ResouceIcon/IconSendOutline';
import IconExternalSmall from '@assets/components/ResouceIcon/IconExternalSmall';
import {CAMPAIGN_TEMPLATE_ID} from '@assets/const/campaignTemplate';
import './WidgetWelcomePopup.scss';
import {addCampaignMessages} from '@scripttag/helpers/storage';

const useMuiSx = () => ({
  card: {
    backgroundColor: '#f6f6f6',
    borderRadius: 8,
    p: 1
  },
  inputRoot: {
    backgroundColor: '#f4f4f4',
    borderRadius: '8px',
    paddingLeft: '10px',
    paddingRight: '10px',
    height: 40
  },
  input: {
    p: 0,
    fontSize: 14,
    lineHeight: '40px',
    color: '#737373'
  },
  iconButton: {
    p: 1
  }
});

/**
 * @param {App.WidgetSetting} settings
 * @param shopName
 * @param handleClick
 * @param wizard
 * @param isShowNewsletterPopup
 * @returns {Element}
 * @constructor
 */
export default function WidgetWelcomePopup({
  settings,
  handleClick = () => {},
  addActivity,
  wizard = false,
  isShowNewsletterPopup = false,
  isCartBoosterPopup = false,
  isProductRecommendationPopup = false,
  popupType = CAMPAIGN_TEMPLATE_ID.WELCOME_VISITORS,
  i18nCampaign
}) {
  const sx = useMuiSx();
  const {newsletterPopupSettings, welcomePopupSetting, productPopupSettings} = settings;
  const {i18n} = useContext(FloatButtonContext);

  const {isLabelChanged} = useWidgetLabelChanged();
  const message = useMemo(() => {
    const pathname = window.location.pathname;
    const last = pathname.split('/').pop();
    if (isCartBoosterPopup) {
      return i18nCampaign('message', last);
    }
    if (isProductRecommendationPopup) {
      return i18nCampaign('productMessage', last);
    }
    if (!isLabelChanged && !wizard) {
      return i18n(isShowNewsletterPopup ? 'Teaser message' : 'Welcome visitor teaser');
    }
    return isShowNewsletterPopup
      ? newsletterPopupSettings.teaserMessage
      : welcomePopupSetting.teaserMessage;
  }, [newsletterPopupSettings, welcomePopupSetting, i18n]);

  useEffect(() => {
    addActivity('welcomeVisitorTriggered').then();
  }, []);

  const renderContent = () => {
    switch (popupType) {
      case CAMPAIGN_TEMPLATE_ID.PRODUCT_RECOMMENDATION:
        return (
          <Box>
            <Typography variant="body2" style={{fontSize: '14px', marginBottom: '7px'}}>
              <div
                dangerouslySetInnerHTML={{
                  __html: message?.replaceAll('\n', '<br/>').replaceAll('\\n', '<br/>')
                }}
              />
            </Typography>

            <Card sx={sx.card}>
              <Box sx={{display: 'flex', justifyContent: 'start', gap: '9px'}}>
                <Avatar
                  src={productPopupSettings?.productsList[0]?.img}
                  alt={productPopupSettings?.productsList[0]?.name}
                  sx={{width: 50, height: 50}}
                  variant="rounded"
                />
                <Box>
                  <Typography variant="body2" style={{fontSize: '14px'}}>
                    {productPopupSettings?.productsList[0]?.name}
                  </Typography>
                  <Typography variant="body2" style={{fontSize: '14px', fontWeight: '800'}}>
                    {productPopupSettings?.productsList[0]?.price}
                  </Typography>
                </Box>
              </Box>
              <Box mt={1}>
                <Button
                  fullWidth
                  variant="text"
                  href={productPopupSettings?.productsList[0]?.onlineStoreUrl}
                  target="_blank"
                  style={{height: 40}}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      color: '#232323',
                      alignItems: 'center'
                    }}
                  >
                    <span>{i18n('View product')}</span>
                    <span style={{paddingTop: '1px', display: 'flex', alignItems: 'center'}}>
                      <IconExternalSmall fill="#232323" />
                    </span>
                  </Box>
                </Button>
              </Box>
            </Card>
            <Box
              mt={1.5}
              onClick={e => {
                e.stopPropagation();
                handleClick();
                const product = productPopupSettings?.productsList[0];
                const messages = [
                  {
                    id: 'product-recommendation-question',
                    text: i18n('Hi! Need help with this product?'),
                    isAdmin: true,
                    createdAt: new Date()
                  },
                  {
                    id: 'product-recommendation-show',
                    type: 'product-recommendation-campaign-message',
                    productCampaign: product,
                    text: product?.name || 'Product recommendation message',
                    isAdmin: true,
                    createdAt: new Date()
                  }
                ];
                addCampaignMessages(messages);
              }}
            >
              <TextField
                fullWidth
                placeholder={i18n('Click to reply')}
                variant="filled"
                sx={{
                  '& .MuiFilledInput-root': sx.inputRoot,
                  '& .MuiFilledInput-input': sx.input
                }}
                slotProps={{
                  input: {
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton sx={sx.iconButton}>
                          <IconSendOutline fill="#cccccc" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
              />
            </Box>
          </Box>
        );
      case CAMPAIGN_TEMPLATE_ID.CART_BOOSTER:
        return (
          <Box>
            <Typography variant="body2" style={{fontSize: '14px', marginBottom: '7px'}}>
              <div
                dangerouslySetInnerHTML={{
                  __html: message?.replaceAll('\n', '<br/>').replaceAll('\\n', '<br/>')
                }}
              />
            </Typography>
            <Box mt={1.5}>
              <TextField
                fullWidth
                placeholder={i18n('Click to reply')}
                variant="filled"
                sx={{
                  '& .MuiFilledInput-root': sx.inputRoot,
                  '& .MuiFilledInput-input': sx.input
                }}
                slotProps={{
                  input: {
                    disableUnderline: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton sx={sx.iconButton}>
                          <IconSendOutline fill="#cccccc" />
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
              />
            </Box>
          </Box>
        );
      default:
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: message?.replaceAll('\n', '<br/>').replaceAll('\\n', '<br/>')
            }}
          />
        );
    }
  };

  return (
    <div
      className="Avada-FaqsWelcomePopup__Container"
      style={{backgroundColor: welcomePopupSetting.backgroundColor}}
      onClick={handleClick}
    >
      <div className="AvadaFaqs-MUI-WPMessageTextFont" style={{textAlign: 'left'}}>
        <Typography
          className="Avada-FaqsWelcomePopup__Message"
          variant="div"
          style={{fontSize: '13px', color: welcomePopupSetting.textColor}}
        >
          {renderContent()}
        </Typography>
      </div>
    </div>
  );
}

WidgetWelcomePopup.propTypes = {
  shopName: PropTypes.string,
  settings: PropTypes.object,
  handleClick: PropTypes.func,
  addActivity: PropTypes.func,
  isShowNewsletterPopup: PropTypes.bool,
  popupType: PropTypes.oneOf([
    CAMPAIGN_TEMPLATE_ID.WELCOME_VISITORS,
    CAMPAIGN_TEMPLATE_ID.SUBSCRIBE_NEWSLETTER,
    CAMPAIGN_TEMPLATE_ID.CART_BOOSTER,
    CAMPAIGN_TEMPLATE_ID.PRODUCT_RECOMMENDATION
  ])
};
